import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'

import { LoadingOutlined } from '@ant-design/icons'
import { useThrottleFn } from 'ahooks'
import { Spin } from 'antd'

import { IConfig } from '@/components/Waterfall/Items/Discuss'
import { isSuccess } from '@/httpRequest'
import {
  IGroupMemberItem,
  getGroupMembers
} from '@/httpRequest/modules/imInfo/groupMembers'
import { ISessionHistoryItem } from '@/httpRequest/modules/mesperyIm/sessionHistory'
import { useMessageAtom } from '@/pages/MesperyIM/store/message'
import { useUserAtom } from '@/store/user'

import { MessageItem } from './MessageItem'
import { useHistoryMessages } from '../hooks/useHistoryMessages'
import { MessageEnum } from '../Types'
import { EConversationType, EMessageType } from '../types/message'

export interface IChatPanelContainerRef {
  scrollToBottom: () => void
}

export const SENDER_CUSTOMER_SERVICE_ACCOUNT_MAP = new Map([
  ['AUTHOR_AGENT', import.meta.env.VITE_WS_AUTHOR_AGENT],
  ['CUSTOMER_SERVICE', import.meta.env.VITE_WS_CUSTOMER_SERVICE]
])

export const ChatPanelContainer = () => {
  const { messageData, currentConversation, members, setMembers } =
    useMessageAtom()
  const { user } = useUserAtom()
  const { loadMore, data, loadingMore } = useHistoryMessages()
  const lastScrollDistanceToBottomRef = useRef<number>()
  const lastShowTimeRef = useRef<number>()

  const handleGetGroupMembers = async (group_id: string) => {
    if (!group_id) {
      return
    }
    const resp = await getGroupMembers({ group_id })

    if (isSuccess(resp)) {
      setMembers(resp.data.users)
    }
  }

  const handleShowTime = (messageTime: number) => {
    if (!lastShowTimeRef.current) {
      // 如果是第一次显示时间，则直接显示并更新lastShowTimeRef
      lastShowTimeRef.current = messageTime
      return true
    }

    const timeDiff = (messageTime - lastShowTimeRef.current) / 60 // 计算时间差（分钟）
    if (timeDiff >= 5) {
      // 如果时间差大于或等于5分钟，则显示时间并更新lastShowTimeRef
      lastShowTimeRef.current = messageTime
      return true
    }

    // 如果时间差小于5分钟，则不显示时间
    return false
  }

  const messageList = useMemo(() => {
    return messageData?.list.map((item, index) => {
      if (index === 0) {
        lastShowTimeRef.current = undefined
      }
      item.showTime = handleShowTime(Number(item.created_at))

      return item
    })
  }, [messageData])

  useEffect(() => {
    if (currentConversation?.session_type === EConversationType.GROUP) {
      handleGetGroupMembers(currentConversation?.session_id || '')
    }
  }, [currentConversation?.session_id])

  const membersMap = useMemo(() => {
    const map = new Map<string, IGroupMemberItem>()

    members.forEach((item) => {
      map.set(item.user_id, item)
    })

    return map
  }, [members])

  const getShareMessage = (
    config: IConfig,
    key: number,
    data: ISessionHistoryItem
  ) => {
    if (config.messageType === MessageEnum.Discuss) {
      return (
        <MessageItem
          type={MessageEnum.Discuss}
          self={data.sender_user_id === user.id}
          key={key}
          config={config}
        />
      )
    }

    if (config.messageType === MessageEnum.Story) {
      return (
        <MessageItem
          type={MessageEnum.Story}
          self={data.sender_user_id === user.id}
          key={key}
        />
      )
    }
    if (config.messageType === MessageEnum.Video) {
      return (
        <MessageItem
          type={MessageEnum.Video}
          self={data.sender_user_id === user.id}
          key={key}
        />
      )
    }
    if (config.messageType === MessageEnum.Books) {
      return (
        <MessageItem
          type={MessageEnum.Books}
          self={data.sender_user_id === user.id}
          key={key}
        />
      )
    }
  }

  const getMessageItem = (data: ISessionHistoryItem, key: number) => {
    const senderUserId =
      SENDER_CUSTOMER_SERVICE_ACCOUNT_MAP.get(data.sender_user_id) ||
      data.sender_user_id

    const groupType =
      currentConversation?.session_type === EConversationType.GROUP &&
      senderUserId !== user.id

    const userData =
      groupType && senderUserId !== user.id
        ? { nickname: membersMap.get(senderUserId)?.nickname }
        : {}

    if (data.message_type === EMessageType.TxtMsg) {
      // if (config) {
      //   return getShareMessage(config, key, data)
      // }
      return (
        <MessageItem
          key={key}
          type={'text'}
          self={senderUserId === user.id}
          data={data}
          userData={userData}
          showTime={data.showTime}
        />
      )
    }

    if (data.message_type === EMessageType.ImgMsg) {
      return (
        <MessageItem
          key={key}
          type={'image'}
          self={senderUserId === user.id}
          data={data}
          userData={userData}
          showTime={data.showTime}
        />
      )
    }

    if (data.message_type === EMessageType.SystemMsg) {
      return (
        <MessageItem
          key={key}
          type={'system'}
          self={senderUserId === user.id}
          data={data}
          userData={userData}
          showTime={data.showTime}
        />
      )
    }

    if (data.message_type === 7) {
      return (
        <MessageItem
          key={key}
          type={MessageEnum.NewSystemMsg}
          self={senderUserId === user.id}
          data={data}
          userData={userData}
          showTime={data.showTime}
        />
      )
    }

    return null
  }

  const { run: handleScroll } = useThrottleFn(
    (ev) => {
      const rootNode = ev.target
      if (rootNode) {
        if (rootNode.scrollTop === 0) {
          const scrollDistanceToBottom =
            rootNode.scrollHeight - rootNode.scrollTop
          lastScrollDistanceToBottomRef.current = scrollDistanceToBottom
          loadMore()
        }
      }
    },
    {
      leading: true,
      trailing: true,
      wait: 100
    }
  )

  useLayoutEffect(() => {
    const scrollableRoot = document.getElementById('scrollableDivIM')
    const lastScrollDistanceToBottom =
      lastScrollDistanceToBottomRef.current ?? 0
    if (scrollableRoot) {
      scrollableRoot.scrollTop =
        scrollableRoot.scrollHeight - lastScrollDistanceToBottom
    }
  }, [data])

  useLayoutEffect(() => {
    if (messageList?.length && messageList.length < 20) {
      loadMore()
    }
  }, [messageList])

  useEffect(() => {
    if (currentConversation?.session_type === EConversationType.GROUP) {
      handleGetGroupMembers(currentConversation?.session_id || '')
    }
  }, [currentConversation?.session_id])

  useEffect(() => {
    const nodeEle = document.getElementById('scrollableDivIM')
    nodeEle?.addEventListener('scroll', handleScroll)

    return () => {
      nodeEle?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div id="scrollableDivIM" style={{ overflowY: 'scroll', flex: 1 }}>
      {loadingMore ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '10px'
          }}
        >
          <Spin />
        </div>
      ) : null}
      {/* {data?.list?.map(getMessageItem)} */}
      {messageList?.map(getMessageItem)}
    </div>

    // <div className="ChatPanelContainer" ref={chatPanelRef}>
    //   {messageData?.list.map((item, index) => getMessageItem(item, index))}
    //   {/* <MessageItem type={'text'} self={false} gotoSign={true} />
    // <MessageTime time={'14:00'} />
    // <MessageItem type={'text'} self={true} />
    // <MessageItem type={'text'} self={false} checkIt={true} />
    // <MessageItem type={'image'} self={false} />
    // <MessageItem type={'discuss'} self={false} />
    // <MessageItem type={'story'} self={false} />
    // <MessageItem type={'video'} self={false} />
    // <MessageItem type={'books'} self={false} />
    // <MessageItem type={'reply'} self={false} /> */}
    // </div>
  )
}

ChatPanelContainer.displayName = 'ChatPanelContainer'
